import { useState, useEffect, useCallback, useRef } from 'react';
import './file-explorer.css';
import { useAccessControl } from '../../hooks/useAccessControl';
import { Button } from '../../components/Button';
import { MediaViewer } from './media-viewer';
import { ShareDialog } from './share-dialog';

interface FileExplorerProps {
  onNavigateToLogin?: () => void;
}

interface FileItem {
  key: string;
  size: number;
  uploaded: string;
  contentType: string;
  isFolder?: boolean;
}

interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
}

// 2GB limit in bytes
const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024;
const API_BASE = '/api/files';

// MIME type detection by file extension (fallback)
const getMimeTypeFromExtension = (fileName: string): string => {
  try {
    // Remove any URL encoding and special characters, extract just the extension
    let cleanFileName = fileName;
    
    // Try to decode if it's URL encoded
    try {
      cleanFileName = decodeURIComponent(fileName);
    } catch {
      // If decode fails, use original
    }
    
    // Remove query params
    cleanFileName = cleanFileName.split('?')[0];
    
    // Find the last dot for extension
    const lastDot = cleanFileName.lastIndexOf('.');
    
    if (lastDot === -1 || lastDot === cleanFileName.length - 1) {
      return 'application/octet-stream';
    }
    
    // Extract extension and normalize
    const ext = cleanFileName.substring(lastDot + 1).toLowerCase().trim();
    
    // Remove any non-alphanumeric characters from extension
    const cleanExt = ext.replace(/[^a-z0-9]/gi, '');
    
    const mimeTypes: Record<string, string> = {
      // Images
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
      'bmp': 'image/bmp',
      'ico': 'image/x-icon',
      'tiff': 'image/tiff',
      'heic': 'image/heic',
      // Videos
      'mp4': 'video/mp4',
      'webm': 'video/webm',
      'avi': 'video/x-msvideo',
      'mov': 'video/quicktime',
      'mkv': 'video/x-matroska',
      'flv': 'video/x-flv',
      'm4v': 'video/x-m4v',
      'wmv': 'video/x-ms-wmv',
      '3gp': 'video/3gpp',
      // Audio
      'mp3': 'audio/mpeg',
      'wav': 'audio/wav',
      'ogg': 'audio/ogg',
      'm4a': 'audio/mp4',
      'aac': 'audio/aac',
      'flac': 'audio/flac',
      'wma': 'audio/x-ms-wma',
      // Documents
      'pdf': 'application/pdf',
      'txt': 'text/plain',
      'json': 'application/json',
      'xml': 'application/xml',
      'csv': 'text/csv',
      'zip': 'application/zip',
      'rar': 'application/x-rar-compressed',
      'tar': 'application/x-tar',
      'gz': 'application/gzip',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    };
    
    const mimeType = mimeTypes[cleanExt] || 'application/octet-stream';
    console.log(`[getMimeType] fileName: "${fileName}" -> ext: "${cleanExt}" -> ${mimeType}`);
    return mimeType;
  } catch (error) {
    console.error('[getMimeType] Error:', error);
    return 'application/octet-stream';
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getFileIcon = (contentType: string): string => {
  if (contentType.startsWith('image/')) return '🖼️';
  if (contentType.startsWith('video/')) return '🎬';
  if (contentType.startsWith('audio/')) return '🎵';
  if (contentType.includes('pdf')) return '📄';
  if (contentType.includes('zip') || contentType.includes('rar') || contentType.includes('tar')) return '📦';
  if (contentType.includes('text')) return '📝';
  if (contentType.includes('json') || contentType.includes('xml')) return '📋';
  return '📁';
};

export const FileExplorer = ({ onNavigateToLogin }: FileExplorerProps) => {
  const { isAdmin: isAdminUser, isLoading: isAccessLoading } = useAccessControl();
  const [files, setFiles] = useState<FileItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [mediaViewerIndex, setMediaViewerIndex] = useState(0);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareFileKey, setShareFileKey] = useState<string>('');
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [currentPath, setCurrentPath] = useState('');
  const [copySource, setCopySource] = useState<string | null>(null);
  const [moveSource, setMoveSource] = useState<string | null>(null);
  const [copyMoveDialogOpen, setCopyMoveDialogOpen] = useState(false);
  const [copyMoveMode, setCopyMoveMode] = useState<'copy' | 'move'>('copy');
  const [copyMoveDestination, setCopyMoveDestination] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'size' | 'date'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filterType, setFilterType] = useState<'all' | 'folders' | 'files' | 'images' | 'documents' | 'videos'>('all');
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // Initialize folder input with webkitdirectory attribute
  useEffect(() => {
    if (folderInputRef.current) {
      // Set the webkitdirectory attribute directly on the DOM element
      folderInputRef.current.setAttribute('webkitdirectory', '');
      folderInputRef.current.setAttribute('directory', '');
      console.log('[useEffect] Folder input attributes set:', {
        webkitdirectory: folderInputRef.current.getAttribute('webkitdirectory'),
        directory: folderInputRef.current.getAttribute('directory'),
      });
    }
  }, []);

  // Get breadcrumb items from current path
  const getBreadcrumbs = (): { label: string; path: string }[] => {
    const breadcrumbs = [{ label: 'Root', path: '' }];
    if (currentPath) {
      const parts = currentPath.split('/').filter(Boolean);
      let accumulatedPath = '';
      parts.forEach((part) => {
        accumulatedPath = accumulatedPath ? `${accumulatedPath}/${part}` : part;
        breadcrumbs.push({
          label: part,
          path: accumulatedPath,
        });
      });
    }
    return breadcrumbs;
  };

  // Navigate to a specific folder
  const navigateToFolder = (path: string) => {
    setCurrentPath(path);
    setSelectedFiles(new Set());
    setSearchQuery('');
  };

  // Enter folder when clicked
  const enterFolder = (folderKey: string) => {
    // Handle parent directory (..)
    if (folderKey === '..') {
      if (currentPath) {
        const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/'));
        navigateToFolder(parentPath);
      }
      return;
    }

    const newPath = currentPath ? `${currentPath}/${folderKey}` : folderKey;
    navigateToFolder(newPath);
  };

  // Get root-only files or files in current path
  const getRootLevelItems = (): FileItem[] => {
    // Detect folders by scanning all file paths
    const folders = new Set<string>();
    const items: FileItem[] = [];

    // First pass: detect all folders that should exist
    files.forEach(file => {
      const parts = file.key.split('/');
      
      if (currentPath) {
        // If we're in a subfolder, only process files that belong to this folder
        if (!file.key.startsWith(currentPath + '/')) {
          return;
        }

        // Get path relative to current location
        const relativePath = file.key.substring(currentPath.length + 1);
        const relativeParts = relativePath.split('/');

        // If there's more than one part, it's in a subfolder
        if (relativeParts.length > 1) {
          folders.add(relativeParts[0]);
        } else if (relativeParts[0]) {
          // It's a file at this level
          items.push(file);
        }
      } else {
        // At root level
        if (parts.length > 1) {
          // It's in a subfolder
          folders.add(parts[0]);
        } else {
          // It's a file at root level
          items.push(file);
        }
      }
    });

    // Second pass: add folder items
    folders.forEach(folderName => {
      const folderPath = currentPath ? `${currentPath}/${folderName}` : folderName;
      items.unshift({
        key: folderPath,
        size: 0,
        uploaded: new Date().toISOString(),
        contentType: 'application/folder',
        isFolder: true,
      });
    });

    // Add parent directory (..) if not at root
    if (currentPath) {
      items.unshift({
        key: '..',
        size: 0,
        uploaded: new Date().toISOString(),
        contentType: 'application/folder',
        isFolder: true,
      });
    }

    return items;
  };

  // Fetch files from R2
  const fetchFiles = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}`);
      if (!response.ok) {
        throw new Error('Failed to fetch files');
      }
      const data = await response.json();
      setFiles(data.files || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load files');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdminUser) {
      fetchFiles();
    }
  }, [isAdminUser, fetchFiles]);

  // Upload file to R2
  const uploadFile = async (file: File): Promise<void> => {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`File "${file.name}" exceeds the 2GB limit`);
    }

    // Use browser's MIME type, fallback to extension-based detection
    const mimeType = file.type || getMimeTypeFromExtension(file.name);

    console.log(`[uploadFile] Starting upload - File: ${file.name}, browser type: ${file.type || 'empty'}, detected: ${mimeType}, size: ${file.size}`);

    // Create FormData with the file and explicitly pass the MIME type
    const formData = new FormData();
    formData.append('file', file);
    formData.append('contentType', mimeType); // Send MIME type separately

    try {
      const response = await fetch(`${API_BASE}`, {
        method: 'POST',
        body: formData,
      });

      console.log(`[uploadFile] Response status: ${response.status} for file ${file.name}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`[uploadFile] Upload failed - Status: ${response.status}, Error:`, errorData);
        throw new Error(errorData.error || `Upload failed with status ${response.status}`);
      }

      const result = await response.json();
      console.log(`[uploadFile] Success - stored as contentType: ${result.file?.contentType || 'unknown'} for file ${file.name}`);
    } catch (error) {
      console.error(`[uploadFile] Error uploading ${file.name}:`, error);
      throw error;
    }
  };

  // Handle file selection
  const handleFileSelect = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) {
      console.log('[handleFileSelect] No files selected');
      return;
    }

    const filesToUpload = Array.from(fileList);
    console.log(`[handleFileSelect] Selected ${filesToUpload.length} files/items:`, filesToUpload.map(f => ({ name: f.name, size: f.size, type: f.type })));
    
    // Validate file sizes
    const oversizedFiles = filesToUpload.filter(f => f.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setMessage({
        type: 'error',
        text: `${oversizedFiles.length} file(s) exceed the 2GB limit and will be skipped`,
      });
    }

    const validFiles = filesToUpload.filter(f => f.size <= MAX_FILE_SIZE);
    console.log(`[handleFileSelect] Valid files after size check: ${validFiles.length}`);
    if (validFiles.length === 0) return;

    // Initialize progress tracking
    const progressList: UploadProgress[] = validFiles.map(f => ({
      fileName: f.name,
      progress: 0,
      status: 'pending',
    }));
    setUploadProgress(progressList);

    // Upload files sequentially
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      setUploadProgress(prev => 
        prev.map((p, idx) => 
          idx === i ? { ...p, status: 'uploading', progress: 50 } : p
        )
      );

      try {
        await uploadFile(file);
        setUploadProgress(prev =>
          prev.map((p, idx) =>
            idx === i ? { ...p, status: 'completed', progress: 100 } : p
          )
        );
      } catch (err) {
        setUploadProgress(prev =>
          prev.map((p, idx) =>
            idx === i
              ? { ...p, status: 'error', error: err instanceof Error ? err.message : 'Upload failed' }
              : p
          )
        );
      }
    }

    // Refresh file list and show success message
    await fetchFiles();
    const successCount = uploadProgress.filter(p => p.status === 'completed').length;
    if (successCount > 0) {
      setMessage({ type: 'success', text: `Successfully uploaded ${successCount} file(s)` });
    }

    // Reset file inputs to allow re-selecting the same folder/files
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (folderInputRef.current) {
      folderInputRef.current.value = '';
    }

    // Clear progress after delay
    setTimeout(() => setUploadProgress([]), 3000);
  };

  // Download file
  const downloadFile = async (key: string) => {
    try {
      const response = await fetch(`${API_BASE}/download/${encodeURIComponent(key)}`);
      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = key;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: `Downloaded: ${key}` });
    } catch (err) {
      setMessage({ type: 'error', text: `Failed to download: ${err instanceof Error ? err.message : 'Unknown error'}` });
    }
  };

  // Delete file
  const deleteFile = async (key: string) => {
    if (!confirm(`Are you sure you want to delete "${key}"?`)) return;

    try {
      const response = await fetch(`${API_BASE}/delete/${encodeURIComponent(key)}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      setFiles(prev => prev.filter(f => f.key !== key));
      setSelectedFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
      setMessage({ type: 'success', text: `Deleted: ${key}` });
    } catch (err) {
      setMessage({ type: 'error', text: `Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}` });
    }
  };

  // Delete selected files
  const deleteSelectedFiles = async () => {
    if (selectedFiles.size === 0) return;
    if (!confirm(`Are you sure you want to delete ${selectedFiles.size} file(s)?`)) return;

    let successCount = 0;
    for (const key of selectedFiles) {
      try {
        const response = await fetch(`${API_BASE}/delete/${encodeURIComponent(key)}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          successCount++;
        }
      } catch {
        // Continue with other files
      }
    }

    await fetchFiles();
    setSelectedFiles(new Set());
    setMessage({ type: 'success', text: `Deleted ${successCount} file(s)` });
  };

  // Open share dialog for single file
  const openShareDialog = (fileKey: string) => {
    setShareFileKey(fileKey);
    setShareDialogOpen(true);
  };

  // Open share dialog for selected files
  const shareSelectedFiles = () => {
    if (selectedFiles.size === 0) return;
    const firstFile = Array.from(selectedFiles)[0];
    openShareDialog(firstFile);
  };

  // Fix MIME types for selected files
  const fixMimeTypesForSelectedFiles = async () => {
    if (selectedFiles.size === 0) return;
    if (!confirm(`Fix MIME types for ${selectedFiles.size} file(s)?`)) return;

    let successCount = 0;
    for (const key of selectedFiles) {
      try {
        const detectedMimeType = getMimeTypeFromExtension(key);
        const response = await fetch(`${API_BASE}/fix-mime/${encodeURIComponent(key)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ contentType: detectedMimeType }),
        });
        if (response.ok) {
          successCount++;
        }
      } catch {
        // Continue with other files
      }
    }

    await fetchFiles();
    setSelectedFiles(new Set());
    setMessage({ type: 'success', text: `Fixed MIME type for ${successCount} file(s)` });
  };

  // Create new folder
  const createNewFolder = async () => {
    if (!newFolderName.trim()) {
      setMessage({ type: 'error', text: 'Folder name cannot be empty' });
      return;
    }

    const folderPath = currentPath ? `${currentPath}/${newFolderName}` : newFolderName;

    try {
      const response = await fetch(`${API_BASE}/folder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ folderPath }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create folder');
      }

      setMessage({ type: 'success', text: `Created folder: ${folderPath}` });
      setNewFolderName('');
      setCreateFolderOpen(false);
      await fetchFiles();
    } catch (err) {
      setMessage({
        type: 'error',
        text: `Failed to create folder: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    }
  };

  // Copy file/folder
  const copyFile = async (source: string) => {
    setCopySource(source);
    setCopyMoveMode('copy');
    setCopyMoveDestination(currentPath);
    setCopyMoveDialogOpen(true);
  };

  // Move file/folder
  const moveFile = async (source: string) => {
    setMoveSource(source);
    setCopyMoveMode('move');
    setCopyMoveDestination(currentPath);
    setCopyMoveDialogOpen(true);
  };

  // Execute copy operation
  const executeCopy = async () => {
    if (!copySource) return;

    try {
      const response = await fetch(`${API_BASE}/copy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source: copySource,
          destination: copyMoveDestination,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Copy failed');
      }

      setMessage({ type: 'success', text: `Copied: ${copySource}` });
      setCopySource(null);
      setCopyMoveDialogOpen(false);
      setCopyMoveDestination('');
      await fetchFiles();
    } catch (err) {
      setMessage({
        type: 'error',
        text: `Failed to copy: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    }
  };

  // Execute move operation
  const executeMove = async () => {
    if (!moveSource) return;

    try {
      const response = await fetch(`${API_BASE}/move`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source: moveSource,
          destination: copyMoveDestination,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Move failed');
      }

      setMessage({ type: 'success', text: `Moved: ${moveSource}` });
      setMoveSource(null);
      setCopyMoveDialogOpen(false);
      setCopyMoveDestination('');
      setSelectedFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(moveSource);
        return newSet;
      });
      await fetchFiles();
    } catch (err) {
      setMessage({
        type: 'error',
        text: `Failed to move: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  // Toggle file selection
  const toggleFileSelection = (key: string) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Select all files
  const toggleSelectAll = () => {
    if (selectedFiles.size === filteredFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(filteredFiles.map(f => f.key)));
    }
  };

  // Helper function to get file category
  const getFileCategory = (contentType: string, isFolder: boolean): string => {
    if (isFolder) return 'folders';
    if (contentType.startsWith('image/')) return 'images';
    if (contentType.startsWith('video/')) return 'videos';
    if (contentType.includes('pdf') || contentType.includes('document') || contentType.includes('sheet') || contentType.includes('presentation')) return 'documents';
    return 'files';
  };

  // Filter and sort files
  let processedFiles = getRootLevelItems()
    .filter(f => f.key.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter(f => {
      if (filterType === 'all') return true;
      if (filterType === 'folders') return f.isFolder;
      if (filterType === 'files') return !f.isFolder;
      const category = getFileCategory(f.contentType, f.isFolder);
      return category === filterType;
    })
    .sort((a, b) => {
      let compareValue = 0;

      if (sortBy === 'name') {
        const aName = a.key.split('/').pop() || '';
        const bName = b.key.split('/').pop() || '';
        compareValue = aName.localeCompare(bName);
      } else if (sortBy === 'size') {
        compareValue = a.size - b.size;
      } else if (sortBy === 'date') {
        compareValue = new Date(a.uploaded).getTime() - new Date(b.uploaded).getTime();
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

  const filteredFiles = processedFiles;

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, fileKey: string) => {
    if (fileKey === '..') {
      e.preventDefault();
      return;
    }
    setDraggedItem(fileKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over folder
  const handleDragOverFolder = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drag enter folder
  const handleDragEnterFolder = (e: React.DragEvent, folderKey: string) => {
    if (folderKey === '..') return;
    e.preventDefault();
    setDragOverFolder(folderKey);
  };

  // Handle drag leave folder
  const handleDragLeaveFolder = () => {
    setDragOverFolder(null);
  };

  // Handle drop on folder
  const handleDropOnFolder = async (e: React.DragEvent, folderKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolder(null);

    if (!draggedItem || folderKey === '..' || draggedItem === folderKey) {
      setDraggedItem(null);
      return;
    }

    // Check if the dragged item is a folder and if we're trying to drop it inside itself
    const draggedItemFolder = files.find(f => f.key === draggedItem)?.isFolder;
    if (draggedItemFolder && folderKey.startsWith(draggedItem + '/')) {
      setMessage({ type: 'error', text: 'Cannot move a folder into itself' });
      setDraggedItem(null);
      return;
    }

    // Move the file to the new location
    const newPath = currentPath ? `${currentPath}/${folderKey}` : folderKey;
    setCopyMoveMode('move');
    setCopyMoveDestination(newPath);

    try {
      const response = await fetch(`${API_BASE}/move`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source: draggedItem,
          destination: newPath,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Move failed');
      }

      setMessage({ type: 'success', text: `Moved to ${folderKey}` });
      await fetchFiles();
    } catch (err) {
      setMessage({
        type: 'error',
        text: `Failed to move: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    }

    setDraggedItem(null);
  };

  // Filter files by search query
  const filteredFilesBackup = getRootLevelItems().filter(f =>
    f.key.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Show loading state
  if (isAccessLoading) {
    return (
      <div className="file-explorer-container">
        <div className="file-explorer-loading">
          <div className="loading-spinner"></div>
          <p>Checking access...</p>
        </div>
      </div>
    );
  }

  // Show access denied for non-admins
  if (!isAdminUser) {
    return (
      <div className="file-explorer-container">
        <div className="file-explorer-not-found">
          <div className="not-found-icon">🔒</div>
          <h2 className="not-found-title">Access Denied</h2>
          <p className="not-found-subtitle">
            This page is only accessible to administrators.
          </p>
          {onNavigateToLogin && (
            <Button label="Go to Login" onClick={onNavigateToLogin} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="file-explorer-container">
      <header className="file-explorer-header">
        <h1 className="file-explorer-title">File Explorer</h1>
        <p className="file-explorer-subtitle">
          Upload and manage files stored in Cloudflare R2 (Max 2GB per file)
        </p>
      </header>

      {/* Alert Messages */}
      {message && (
        <div className={`file-explorer-alert alert-${message.type}`}>
          <span className="alert-icon">{message.type === 'success' ? '✓' : '✕'}</span>
          <span className="alert-text">{message.text}</span>
          <button className="alert-close" onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      {/* Upload Area */}
      <div
        className={`file-explorer-upload-area ${isDragging ? 'dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={(e) => handleFileSelect(e.target.files)}
          style={{ display: 'none' }}
        />
        <input
          ref={folderInputRef}
          type="file"
          multiple
          onChange={(e) => handleFileSelect(e.target.files)}
          style={{ display: 'none' }}
        />
        <div className="upload-icon">📤</div>
        <h3 className="upload-title">Drag & Drop files or folders here</h3>
        <p className="upload-subtitle">or click to browse (Max 2GB per file)</p>
        <div className="upload-buttons">
          <button
            className="upload-btn file-upload-btn"
            onClick={() => fileInputRef.current?.click()}
            title="Upload individual files"
          >
            📄 Upload Files
          </button>
          <button
            className="upload-btn folder-upload-btn"
            onClick={() => folderInputRef.current?.click()}
            title="Upload entire folders with their structure"
          >
            📁 Upload Folder
          </button>
        </div>
      </div>

      {/* Upload Progress */}
      {uploadProgress.length > 0 && (
        <div className="file-explorer-progress">
          {uploadProgress.map((p, idx) => (
            <div key={idx} className={`progress-item progress-${p.status}`}>
              <span className="progress-name">{p.fileName}</span>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${p.progress}%` }}></div>
              </div>
              <span className="progress-status">
                {p.status === 'pending' && '⏳'}
                {p.status === 'uploading' && '⬆️'}
                {p.status === 'completed' && '✅'}
                {p.status === 'error' && '❌'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* File List Card */}
      <div className="file-explorer-card">
        {/* Breadcrumb Navigation */}
        {currentPath && (
          <div className="file-explorer-breadcrumb">
            {getBreadcrumbs().map((crumb, index) => (
              <div key={crumb.path || 'root'} className="breadcrumb-item">
                <button
                  className="breadcrumb-link"
                  onClick={() => navigateToFolder(crumb.path)}
                  disabled={currentPath === crumb.path}
                >
                  {crumb.label}
                </button>
                {index < getBreadcrumbs().length - 1 && (
                  <span className="breadcrumb-separator">/</span>
                )}
              </div>
            ))}
          </div>
        )}
        
        <div className="file-explorer-card-header">
          <h2 className="file-explorer-card-title">Files ({filteredFiles.length})</h2>
          <div className="file-explorer-actions">
            <input
              type="text"
              className="file-explorer-search"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            
            {/* Filter Dropdown */}
            <select
              className="action-select filter-select"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
            >
              <option value="all">📋 All</option>
              <option value="folders">📁 Folders</option>
              <option value="files">📄 Files</option>
              <option value="images">🖼️ Images</option>
              <option value="documents">📝 Documents</option>
              <option value="videos">🎬 Videos</option>
            </select>

            {/* Sort Dropdown */}
            <select
              className="action-select sort-select"
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [by, order] = e.target.value.split('-');
                setSortBy(by as any);
                setSortOrder(order as any);
              }}
            >
              <option value="name-asc">↑ Name (A-Z)</option>
              <option value="name-desc">↓ Name (Z-A)</option>
              <option value="date-desc">📅 Newest First</option>
              <option value="date-asc">📅 Oldest First</option>
              <option value="size-desc">📊 Largest First</option>
              <option value="size-asc">📊 Smallest First</option>
            </select>
            
            <button
              className="action-btn new-folder-btn"
              onClick={() => setCreateFolderOpen(true)}
            >
              📁 New Folder
            </button>
            <button
              className="action-btn refresh-btn"
              onClick={fetchFiles}
              disabled={isLoading}
            >
              🔄 Refresh
            </button>
            {selectedFiles.size > 0 && (
              <>
                <button
                  className="action-btn copy-btn"
                  onClick={() => {
                    const firstFile = Array.from(selectedFiles)[0];
                    copyFile(firstFile);
                  }}
                >
                  📋 Copy ({selectedFiles.size})
                </button>
                <button
                  className="action-btn move-btn"
                  onClick={() => {
                    const firstFile = Array.from(selectedFiles)[0];
                    moveFile(firstFile);
                  }}
                >
                  ✂️ Move ({selectedFiles.size})
                </button>
                <button
                  className="action-btn share-btn"
                  onClick={shareSelectedFiles}
                >
                  🔗 Share ({selectedFiles.size})
                </button>
                <button
                  className="action-btn fix-mime-btn"
                  onClick={fixMimeTypesForSelectedFiles}
                >
                  🔧 Fix MIME Type ({selectedFiles.size})
                </button>
                <button
                  className="action-btn delete-btn"
                  onClick={deleteSelectedFiles}
                >
                  🗑️ Delete Selected ({selectedFiles.size})
                </button>
              </>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="file-explorer-loading">
            <div className="loading-spinner"></div>
            <p>Loading files...</p>
          </div>
        ) : error ? (
          <div className="file-explorer-error">
            <p>❌ {error}</p>
            <button onClick={fetchFiles}>Try Again</button>
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="file-explorer-empty">
            <div className="empty-icon">📂</div>
            <p>No files found</p>
            <p className="empty-hint">Upload files using the area above</p>
          </div>
        ) : (
          <div className="file-explorer-table">
            <div className="file-table-header">
              <div className="file-col file-col-select">
                <input
                  type="checkbox"
                  checked={selectedFiles.size === filteredFiles.length && filteredFiles.length > 0}
                  onChange={toggleSelectAll}
                />
              </div>
              <div className="file-col file-col-name">Name</div>
              <div className="file-col file-col-size">Size</div>
              <div className="file-col file-col-type">Type</div>
              <div className="file-col file-col-date">Uploaded</div>
              <div className="file-col file-col-actions">Actions</div>
            </div>
            <div className="file-table-body">
              {filteredFiles.map((file) => (
                <div
                  key={file.key}
                  className={`file-table-row ${selectedFiles.has(file.key) ? 'selected' : ''} ${file.key === '..' ? 'parent-row' : ''} ${dragOverFolder === file.key && file.isFolder ? 'drag-over' : ''}`}
                  draggable={file.key !== '..' && !file.isFolder}
                  onDragStart={(e) => handleDragStart(e, file.key)}
                  onDragOver={file.isFolder ? handleDragOverFolder : undefined}
                  onDragEnter={file.isFolder ? (e) => handleDragEnterFolder(e, file.key) : undefined}
                  onDragLeave={handleDragLeaveFolder}
                  onDrop={file.isFolder ? (e) => handleDropOnFolder(e, file.key) : undefined}
                  style={{
                    cursor: file.key !== '..' && !file.isFolder ? 'grab' : 'default',
                    opacity: draggedItem === file.key ? 0.5 : 1,
                  }}
                >
                  <div className="file-col file-col-select">
                    {file.key !== '..' && (
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(file.key)}
                        onChange={() => toggleFileSelection(file.key)}
                      />
                    )}
                  </div>
                  <div className="file-col file-col-name">
                    <span className="file-icon">{file.isFolder ? (file.key === '..' ? '⬆️' : '📁') : getFileIcon(file.contentType)}</span>
                    {file.isFolder ? (
                      <button
                        className={`file-name-btn folder-btn ${file.key === '..' ? 'parent-folder-btn' : ''}`}
                        onClick={() => {
                          enterFolder(file.key);
                        }}
                        title={file.key === '..' ? 'Go to parent folder' : `Open folder: ${file.key}`}
                      >
                        {file.key === '..' ? '..' : (currentPath ? file.key.substring(currentPath.length + 1) : file.key)}
                      </button>
                    ) : (file.contentType.startsWith('image/') || file.contentType.startsWith('video/')) ? (
                      <button
                        className="file-name-btn"
                        onClick={() => {
                          const index = files.findIndex((f) => f.key === file.key);
                          setMediaViewerIndex(index);
                          setMediaViewerOpen(true);
                        }}
                        title={`Click to view: ${file.key}`}
                      >
                        {currentPath ? file.key.substring(currentPath.length + 1) : file.key}
                      </button>
                    ) : (
                      <span className="file-name" title={file.key}>
                        {currentPath ? file.key.substring(currentPath.length + 1) : file.key}
                      </span>
                    )}
                  </div>
                  <div className="file-col file-col-size">{file.isFolder ? '—' : formatFileSize(file.size)}</div>
                  <div className="file-col file-col-type">{file.isFolder ? 'Folder' : file.contentType}</div>
                  <div className="file-col file-col-date">{formatDate(file.uploaded)}</div>
                  <div className="file-col file-col-actions">
                    {file.key !== '..' && (
                      <>
                        {!file.isFolder && (
                          <>
                            <button
                              className="file-action-btn copy-btn"
                              onClick={() => copyFile(file.key)}
                              title="Copy"
                            >
                              📋
                            </button>
                            <button
                              className="file-action-btn move-btn"
                              onClick={() => moveFile(file.key)}
                              title="Move"
                            >
                              ✂️
                            </button>
                            <button
                              className="file-action-btn share-btn"
                              onClick={() => openShareDialog(file.key)}
                              title="Share"
                            >
                              🔗
                            </button>
                            <button
                              className="file-action-btn download-btn"
                              onClick={() => downloadFile(file.key)}
                              title="Download"
                            >
                              ⬇️
                            </button>
                          </>
                        )}
                        <button
                          className="file-action-btn delete-btn"
                          onClick={() => deleteFile(file.key)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Share Dialog */}
      <ShareDialog
        isOpen={shareDialogOpen}
        fileName={shareFileKey}
        fileKey={shareFileKey}
        onClose={() => {
          setShareDialogOpen(false);
          setShareFileKey('');
        }}
      />

      {/* Create Folder Dialog */}
      {createFolderOpen && (
        <div className="dialog-overlay" onClick={() => setCreateFolderOpen(false)}>
          <div className="dialog-box" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3 className="dialog-title">Create New Folder</h3>
              <button
                className="dialog-close"
                onClick={() => setCreateFolderOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="dialog-content">
              <label className="dialog-label">Folder Name</label>
              <input
                type="text"
                className="dialog-input"
                placeholder="Enter folder name..."
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') createNewFolder();
                }}
              />
              {currentPath && (
                <p className="dialog-info">📍 Location: {currentPath}</p>
              )}
            </div>
            <div className="dialog-footer">
              <button
                className="dialog-btn dialog-btn-secondary"
                onClick={() => setCreateFolderOpen(false)}
              >
                Cancel
              </button>
              <button
                className="dialog-btn dialog-btn-primary"
                onClick={createNewFolder}
              >
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy/Move Dialog */}
      {copyMoveDialogOpen && (
        <div className="dialog-overlay" onClick={() => setCopyMoveDialogOpen(false)}>
          <div className="dialog-box" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3 className="dialog-title">
                {copyMoveMode === 'copy' ? 'Copy File/Folder' : 'Move File/Folder'}
              </h3>
              <button
                className="dialog-close"
                onClick={() => setCopyMoveDialogOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="dialog-content">
              <label className="dialog-label">Source</label>
              <p className="dialog-info">
                {copyMoveMode === 'copy' ? copySource : moveSource}
              </p>
              <label className="dialog-label">Destination Folder</label>
              <input
                type="text"
                className="dialog-input"
                placeholder="Leave empty for root..."
                value={copyMoveDestination}
                onChange={(e) => setCopyMoveDestination(e.target.value)}
                autoFocus
              />
            </div>
            <div className="dialog-footer">
              <button
                className="dialog-btn dialog-btn-secondary"
                onClick={() => setCopyMoveDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                className="dialog-btn dialog-btn-primary"
                onClick={
                  copyMoveMode === 'copy' ? executeCopy : executeMove
                }
              >
                {copyMoveMode === 'copy' ? 'Copy' : 'Move'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media Viewer Modal */}
      <MediaViewer
        files={files}
        isOpen={mediaViewerOpen}
        initialIndex={mediaViewerIndex}
        onClose={() => setMediaViewerOpen(false)}
      />
    </div>
  );
};

export default FileExplorer;
